import { useCallback, useEffect, useRef, useState } from "react";

type SpeechState = {
  /** 現在認識中（マイクが ON）か */
  listening: boolean;
  /** 直近の確定済み認識テキスト */
  transcript: string;
  /** 認識中の暫定テキスト */
  interim: string;
  /** ブラウザがサポートしているか */
  supported: boolean;
  /** エラーメッセージ */
  error: string | null;
};

type UseSpeechRecognitionResult = SpeechState & {
  start: () => void;
  stop: () => void;
  reset: () => void;
};

/** 開始直後、一度も声が入らないまま無音が続いたら stop() するまでの猶予（ms） */
const LEADING_SILENCE_MS = 4000;
/** 発話後、最後の認識結果からこの時間（ms）無音が続いたら stop() する */
const TRAILING_SILENCE_MS = 1500;
/**
 * 無音による自動 stop 直後、start() を無視する猶予（ms）。
 * 「とめる」ボタンと自動停止のタイミングが重なると、押した瞬間に
 * ボタンが「はなす」に切り替わり、そのままクリックが再開始として
 * 扱われてしまうのを防ぐ。
 */
const AUTO_STOP_COOLDOWN_MS = 1000;

/**
 * Web Speech API のフック（ja-JP 固定）
 *
 * iOS Safari / Android Chrome / デスクトップ Chrome で動作。
 * Firefox は未対応のため supported=false が返る。
 *
 * iOS Safari ではブラウザ内蔵の無音タイムアウト（no-speech エラー）経由だと
 * マイクが解放されないことがある一方、明示的な stop() 呼び出しでは解放される
 * ことを確認済みのため、無音自動タイムアウトには頼らずアプリ側のタイマーで
 * 先回りして stop() する（ユーザーが「とめる」を押すのと同じ経路）。
 */
export const useSpeechRecognition = (): UseSpeechRecognitionResult => {
  const [state, setState] = useState<SpeechState>({
    listening: false,
    transcript: "",
    interim: "",
    supported: false,
    error: null,
  });

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** stop() がユーザー操作（またはコード）から明示的に呼ばれたか（＝無音タイマー経由ではないか） */
  const manualStopRef = useRef(false);
  /** この時刻（epoch ms）までは start() を無視する */
  const startBlockedUntilRef = useRef(0);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!Ctor) {
      setState((s) => ({ ...s, supported: false }));
      return;
    }
    const recognition = new Ctor();
    recognition.lang = "ja-JP";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    const armSilenceTimer = (delayMs: number) => {
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        manualStopRef.current = false;
        try {
          recognition.stop();
        } catch {
          // 既に終了済みなら何もしない
        }
      }, delayMs);
    };

    recognition.onaudiostart = () => {
      armSilenceTimer(LEADING_SILENCE_MS);
    };

    recognition.onresult = (event) => {
      armSilenceTimer(TRAILING_SILENCE_MS);
      let interim = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const alt = result[0];
        if (!alt) continue;
        if (result.isFinal) {
          finalText += alt.transcript;
        } else {
          interim += alt.transcript;
        }
      }
      setState((s) => ({
        ...s,
        transcript: finalText ? s.transcript + finalText : s.transcript,
        interim,
      }));
    };

    recognition.onerror = (event) => {
      clearSilenceTimer();
      setState((s) => ({ ...s, error: event.error, listening: false }));
    };

    recognition.onend = () => {
      clearSilenceTimer();
      if (!manualStopRef.current) {
        // ユーザーの明示的な stop() を経由しない終了（無音タイマー等）は、
        // 直後に「とめる」ボタンが押されても再開始しないよう一定時間ブロックする
        startBlockedUntilRef.current = Date.now() + AUTO_STOP_COOLDOWN_MS;
      }
      manualStopRef.current = false;
      setState((s) => ({
        ...s,
        listening: false,
        // stop() が確定結果を待たずに end へ進むことがあるため、
        // 残っていた暫定テキストを捨てずに確定テキストへ救済する
        transcript: s.interim ? s.transcript + s.interim : s.transcript,
        interim: "",
      }));
    };

    recognitionRef.current = recognition;
    setState((s) => ({ ...s, supported: true }));

    return () => {
      clearSilenceTimer();
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [clearSilenceTimer]);

  const start = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    if (Date.now() < startBlockedUntilRef.current) return;
    setState((s) => ({
      ...s,
      listening: true,
      error: null,
      transcript: "",
      interim: "",
    }));
    try {
      recognition.start();
    } catch (e) {
      setState((s) => ({
        ...s,
        listening: false,
        error: e instanceof Error ? e.message : "start failed",
      }));
    }
  }, []);

  const stop = useCallback(() => {
    clearSilenceTimer();
    manualStopRef.current = true;
    recognitionRef.current?.stop();
  }, [clearSilenceTimer]);

  const reset = useCallback(() => {
    setState((s) => ({ ...s, transcript: "", interim: "", error: null }));
  }, []);

  return { ...state, start, stop, reset };
};
